// Book cover field — file upload + URL paste + one-click AUTO-GENERATED
// themed cover. Used by both the Upload and Edit dialogs in Book Club so the
// UX stays identical everywhere and every book on the wall can look JJU
// branded even when the member has no design skills.

import { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Image as ImageIcon, Sparkles, Loader2, X } from "lucide-react";
import { POST_THEMES, getPostTheme } from "@/lib/postThemes";

async function uploadImageBlob(blob, filename) {
  const form = new FormData();
  form.append("file", new File([blob], filename, { type: "image/png" }));
  // Let the browser set the multipart boundary automatically — do not
  // override Content-Type.
  const { data } = await api.post("/files/upload", form);
  return `/api/files/${data.path}`;
}

function GenerateModal({ open, defaults, onClose, onGenerated }) {
  const previewRef = useRef(null);
  const [themeId, setThemeId] = useState("royal");
  const [verse, setVerse] = useState("");
  const [busy, setBusy] = useState(false);
  const theme = getPostTheme(themeId);
  const title = defaults?.title || "Untitled";
  const author = defaults?.author || "Unknown";

  // Escape closes the modal for keyboard users.
  useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [open, onClose]);

  if (!open) return null;

  const generate = async () => {
    if (!previewRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(previewRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        skipFonts: true,
      });
      const blob = await (await fetch(dataUrl)).blob();
      const url = await uploadImageBlob(blob, `cover-${Date.now()}.png`);
      onGenerated(url);
      toast.success("Cover generated");
      onClose();
    } catch (e) {
      toast.error("Could not generate cover");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cover-gen-title"
      data-testid="cover-gen-dialog"
    >
      <div className="jj-card p-6 max-w-md w-full relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-[var(--jj-surface)]" aria-label="Close">
          <X className="h-4 w-4" strokeWidth={1.6} />
        </button>
        <div className="overline text-burgundy">Auto-cover</div>
        <h3 id="cover-gen-title" className="font-serif text-2xl text-jjblue mt-1">Design a themed cover</h3>
        <p className="text-sm text-[var(--jj-muted)] mt-1">
          Pick a background, add an optional verse — we'll compose it for you.
        </p>

        <div className="mt-4 flex justify-center">
          <div
            ref={previewRef}
            className="relative"
            style={{
              width: 260,
              height: 400,
              background: theme.background,
              color: theme.text,
              borderRadius: 10,
              padding: 22,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              boxShadow: "0 6px 24px rgba(0,0,0,0.15)",
              overflow: "hidden",
            }}
            data-testid="cover-gen-preview"
          >
            <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", opacity: 0.75, fontWeight: 600 }}>
              Jesus Just Us · Book Club
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div
                style={{
                  fontFamily: "serif",
                  fontSize: title.length > 30 ? 22 : 28,
                  lineHeight: 1.15,
                  fontWeight: 600,
                }}
              >
                {title}
              </div>
              <div style={{ fontSize: 12, opacity: 0.85, letterSpacing: "0.06em" }}>by {author}</div>
              {verse.trim() && (
                <div
                  style={{
                    marginTop: 12,
                    borderTop: `1px solid ${theme.text}`,
                    paddingTop: 10,
                    fontFamily: "serif",
                    fontSize: 12,
                    fontStyle: "italic",
                    lineHeight: 1.4,
                    opacity: 0.9,
                  }}
                >
                  {verse.trim()}
                </div>
              )}
            </div>
            <div style={{ fontSize: 9, opacity: 0.7, letterSpacing: "0.12em", textTransform: "uppercase" }}>
              jesusjustus.us
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="text-xs text-[var(--jj-muted)] mb-1.5">Background</div>
          <div className="flex flex-wrap gap-2" data-testid="cover-gen-theme-picker">
            {POST_THEMES.filter((t) => t.id !== "none").map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setThemeId(t.id)}
                title={t.label}
                aria-label={`Background ${t.label}`}
                className={`h-8 w-8 rounded-full border-2 transition ${themeId === t.id ? "border-[var(--jj-blue)] scale-110" : "border-white hover:scale-105"} shadow-sm`}
                style={{ background: t.swatch }}
                data-testid={`cover-gen-theme-${t.id}`}
              />
            ))}
          </div>
        </div>

        <div className="mt-3">
          <label className="text-xs text-[var(--jj-muted)]">Scripture reference (optional)</label>
          <input
            className="input-jj mt-1 !py-1.5 text-xs"
            placeholder='e.g. "Be still, and know that I am God." — Psalm 46:10'
            maxLength={140}
            value={verse}
            onChange={(e) => setVerse(e.target.value)}
            data-testid="cover-gen-verse"
          />
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
          <button
            onClick={generate}
            disabled={busy}
            className="btn-primary !py-2 !px-4 text-sm flex items-center gap-2"
            data-testid="cover-gen-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" strokeWidth={1.6} />}
            {busy ? "Generating…" : "Use this cover"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function CoverField({ value, onChange, defaults, testIdPrefix = "book-cover" }) {
  const [uploading, setUploading] = useState(false);
  const [genOpen, setGenOpen] = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Cover too large (max 8MB)."); return; }
    setUploading(true);
    try {
      const url = await uploadImageBlob(f, f.name);
      onChange(url);
      toast.success("Cover uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="rounded-lg border border-dashed border-jj bg-cream-surface p-3">
      <div className="flex items-center gap-3">
        <div className="h-16 w-12 rounded overflow-hidden bg-white/60 flex items-center justify-center flex-shrink-0">
          {value ? (
            <img src={value} alt="cover preview" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="h-5 w-5 text-burgundy/40" strokeWidth={1.6} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-jjblue/85 font-medium">Cover image</div>
          <div className="text-[11px] text-[var(--jj-muted)]">Upload, paste a URL, or auto-generate a themed cover.</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            <label className={`btn-secondary !py-1 !px-2.5 text-[11px] cursor-pointer ${uploading ? "opacity-60" : ""}`}>
              {uploading ? "Uploading…" : (value ? "Replace" : "Choose image")}
              <input type="file" accept="image/*" onChange={handleFile} disabled={uploading} className="hidden" data-testid={`${testIdPrefix}-file`} />
            </label>
            <button
              type="button"
              onClick={() => setGenOpen(true)}
              className="btn-secondary !py-1 !px-2.5 text-[11px] flex items-center gap-1"
              data-testid={`${testIdPrefix}-generate`}
            >
              <Sparkles className="h-3 w-3" strokeWidth={1.6} /> Generate
            </button>
          </div>
        </div>
      </div>
      <input
        className="input-jj mt-2 !py-1.5 text-xs"
        placeholder="…or paste an image URL"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testIdPrefix}
      />
      <GenerateModal
        open={genOpen}
        defaults={defaults}
        onClose={() => setGenOpen(false)}
        onGenerated={onChange}
      />
    </div>
  );
}
